import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { buildCdnURL } from "@/utils/utils";
import { cn } from "@/utils/cn";
import { SUPPORT_IMAGE_URL } from "@/utils/constants";

const ImageSlider = ({
  images = [],
  alt = "",
  className = "",
  onClick = () => {},
  showNavigation = true,
  showDots = true,
  autoSlide = false,
  autoSlideInterval = 3000,
  disabled = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);
  const thumbnailScrollRef = useRef(null);
  const isProgrammaticScroll = useRef(false);

  // Filter out invalid images and ensure we have at least one image
  const validImages = images?.filter((img) => img) || [];
  const imageUrls =
    validImages.length > 0
      ? validImages.map((img) => buildCdnURL(img))
      : ["/assets/placeholder.webp"];

  const totalImages = imageUrls.length;
  const hasMultipleImages = totalImages > 1;

  // Filter out support image for thumbnails
  const thumbnailImages = validImages.filter(
    (img) => img !== SUPPORT_IMAGE_URL
  );
  const hasThumbnails = thumbnailImages.length > 1;

  // Auto slide functionality
  useEffect(() => {
    if (!autoSlide || !hasMultipleImages || disabled) return;

    const interval = setInterval(() => {
      scrollToIndex((currentIndex + 1) % totalImages);
    }, autoSlideInterval);

    return () => clearInterval(interval);
  }, [
    autoSlide,
    hasMultipleImages,
    currentIndex,
    totalImages,
    autoSlideInterval,
    disabled,
  ]);

  const scrollToIndex = (index) => {
    isProgrammaticScroll.current = true;
    setCurrentIndex(index);

    if (scrollRef.current) {
      const scrollWidth = scrollRef.current.scrollWidth / totalImages;
      scrollRef.current.scrollTo({
        left: scrollWidth * index,
        behavior: "smooth",
      });
    }

    // Scroll thumbnail into view
    if (thumbnailScrollRef.current) {
      const thumbnailElements = thumbnailScrollRef.current.children;
      // Find the actual thumbnail index (excluding SUPPORT_IMAGE_URL)
      let thumbnailIndex = 0;
      let originalIndex = 0;
      for (let i = 0; i <= index && i < validImages.length; i++) {
        if (validImages[i] !== SUPPORT_IMAGE_URL) {
          if (originalIndex === index) break;
          thumbnailIndex++;
        }
        originalIndex++;
      }

      const targetThumbnail = thumbnailElements[thumbnailIndex];
      if (targetThumbnail) {
        targetThumbnail.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }

    // Reset the flag after scroll animation completes (typically ~300-500ms)
    setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 500);
  };

  const goToNext = () => {
    if (disabled || !hasMultipleImages) return;
    const nextIndex = (currentIndex + 1) % totalImages;
    scrollToIndex(nextIndex);
  };

  const goToPrevious = () => {
    if (disabled || !hasMultipleImages) return;
    const prevIndex = (currentIndex - 1 + totalImages) % totalImages;
    scrollToIndex(prevIndex);
  };

  const handleScroll = () => {
    // Don't update index during programmatic scrolling to prevent flickering
    if (isProgrammaticScroll.current) return;

    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const itemWidth = scrollRef.current.scrollWidth / totalImages;
      const index = Math.round(scrollLeft / itemWidth);
      setCurrentIndex(index);
    }
  };

  return (
    <div className={"relative select-none"}>
      {/* Main Image Container */}
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className={cn(
            "flex overflow-x-auto snap-x snap-mandatory no-scrollbar rounded-lg",
            !className.includes("h-") && "h-40"
          )}
          style={{ scrollSnapType: "x mandatory" }}
        >
          {imageUrls.map((url, index) => (
            <div
              key={index}
              className="w-full max-w-screen flex-shrink-0 snap-center"
            >
              <img
                src={url}
                alt={`${alt} ${index + 1}`}
                className={cn(
                  "h-full mx-auto object-contain cursor-pointer p-2",
                  disabled && "opacity-50",
                  className
                )}
                onClick={() => !disabled && onClick()}
                loading="lazy"
                onError={(e) => {
                  e.target.src = "/assets/placeholder.webp";
                }}
                draggable={false}
              />
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        {hasMultipleImages && showNavigation && !disabled && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-black p-2 rounded-full shadow-lg transition-all duration-200 z-10 active:scale-95"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-black p-2 rounded-full shadow-lg transition-all duration-200 z-10 active:scale-95"
              aria-label="Next image"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Image Counter */}
        {hasMultipleImages && (
          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            {currentIndex + 1}/{totalImages}
          </div>
        )}
      </div>

      {/* Thumbnail Images */}
      {hasThumbnails && showDots && !disabled && (
        <div
          ref={thumbnailScrollRef}
          className="flex gap-2 mt-3 overflow-x-auto pb-2 scrollbar-hide px-2 w-screen"
        >
          {imageUrls.map((url, index) => {
            // Skip the support image
            if (validImages[index] === SUPPORT_IMAGE_URL) return null;

            return (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={cn(
                  "flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all duration-200",
                  index === currentIndex
                    ? "border-blue-500 ring-2 ring-blue-200"
                    : "border-gray-200 hover:border-gray-400"
                )}
                aria-label={`Go to image ${index + 1}`}
              >
                <img
                  src={url}
                  alt={`${alt} thumbnail ${index + 1}`}
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    e.target.src = "/assets/placeholder.webp";
                  }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;
